import { ChatServiceInterface, ChatServiceConfig } from './ChatServiceInterface';
import { createFileLogger } from '../../utils/logger';
import { isElectron } from '../../utils/electron';

const log = createFileLogger('CRISP_CHAT_SERVICE');

declare global {
  interface Window {
    $crisp: any;
    CRISP_WEBSITE_ID: string;
  }
}

class CrispChatService implements ChatServiceInterface {
  private isScriptLoaded = false;
  private isServiceReady = false;
  private websiteId: string | null = null;
  private pendingConfig: ChatServiceConfig | null = null;

  async initialize(config: ChatServiceConfig): Promise<void> {
    // In Electron, defer Crisp initialization until the user explicitly opens the
    // chat widget. Loading Crisp eagerly causes ~5s of continuous main-thread work
    // even when the chat is never opened. We store the config and initialize lazily
    // in openChat() on first use.
    if (isElectron()) {
      this.pendingConfig = config;
      log.info('Crisp initialization deferred in Electron — will load on first openChat()');
      return;
    }

    console.log('[Crisp] initialize() called — isServiceReady:', this.isServiceReady, 'websiteId:', config.websiteId);

    if (this.isServiceReady) {
      console.log('[Crisp] Already initialized — skipping');
      log.warn('Crisp chat service already initialized');
      return;
    }

    if (!config.websiteId || config.websiteId.trim() === '') {
      console.error('[Crisp] websiteId is missing or empty');
      throw new Error('Crisp websiteId is missing or empty');
    }

    const websiteIdPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    if (!websiteIdPattern.test(config.websiteId)) {
      console.error('[Crisp] websiteId format is invalid:', config.websiteId);
      throw new Error(`Crisp websiteId format is invalid: ${config.websiteId}`);
    }

    this.websiteId = config.websiteId;
    window.CRISP_WEBSITE_ID = this.websiteId;
    console.log('[Crisp] Set CRISP_WEBSITE_ID =', this.websiteId);

    if (!this.isScriptLoaded) {
      console.log('[Crisp] Loading script...');
      await this.loadCrispScript();
      console.log('[Crisp] Script load complete — window.$crisp type:', typeof window.$crisp, 'isArray:', Array.isArray(window.$crisp));
    }

    if (!window.$crisp) {
      console.error('[Crisp] window.$crisp is null/undefined after script load');
      throw new Error('window.$crisp is not available after script load');
    }

    if (config.userEmail) {
      window.$crisp.push(['set', 'user:email', [config.userEmail]]);
    }
    if (config.userName) {
      window.$crisp.push(['set', 'user:nickname', [config.userName]]);
    }
    if (config.customData) {
      const dataEntries = Object.entries(config.customData).map(([key, value]) => [key, value]);
      window.$crisp.push(['set', 'session:data', [dataEntries]]);
    }

    this.isServiceReady = true;
    console.log('[Crisp] Initialized successfully');
    log.info('Crisp chat service initialized successfully');
  }

  private loadCrispScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Only skip if the Crisp SDK has actually replaced the queue array (i.e. it's not a plain Array).
      // A plain Array means we pre-populated the queue but the script hasn't loaded yet.
      if (this.isScriptLoaded || (window.$crisp && !Array.isArray(window.$crisp))) {
        console.log('[Crisp] Script already loaded — skipping');
        resolve();
        return;
      }

      window.$crisp = [];
      window.$crisp.push(['safe', true]);
      window.$crisp.push(['do', 'chat:hide']);

      window.$crisp.push(['on', 'session:loaded', () => {
        console.log('[Crisp] session:loaded fired — hiding floating button');
        log.debug('Crisp session loaded');
        try {
          // Only hide the floating button — do NOT close the panel.
          // Crisp re-shows its default button after establishing a session, so we
          // hide it again here to keep our own custom trigger as the only entry point.
          // Calling chat:close here would create a race condition where a user who
          // clicked "Get help" before the session finished loading sees the chat
          // immediately get closed.
          window.$crisp.push(['do', 'chat:hide']);
        } catch (e) {
          log.error('Failed to hide Crisp after session load:', e);
        }
      }]);

      const script = document.createElement('script');
      script.src = 'https://client.crisp.chat/l.js';
      script.async = true;

      script.onload = () => {
        this.isScriptLoaded = true;
        console.log('[Crisp] Script onload fired — waiting 200ms then resolving');
        setTimeout(() => resolve(), 200);
      };

      script.onerror = (error) => {
        console.error('[Crisp] Script failed to load (onerror):', error);
        log.error('Failed to load Crisp script:', error);
        // Reset window.$crisp so future initialization attempts don't see the stale [] queue
        // and incorrectly resolve early, creating a false-ready state.
        (window as any).$crisp = undefined;
        reject(new Error('Failed to load Crisp script'));
      };

      document.head.appendChild(script);
    });
  }

  isReady(): boolean {
    return this.isServiceReady && !!window.$crisp;
  }

  openChat(): void {
    // In Electron, Crisp was deferred — initialize now on first use, then open.
    if (isElectron() && !this.isServiceReady && this.pendingConfig) {
      const config = this.pendingConfig;
      this.pendingConfig = null;
      this.initialize(config).then(() => {
        this._doOpenChat();
      }).catch((err) => {
        log.error('Crisp deferred initialization failed:', err);
      });
      return;
    }

    this._doOpenChat();
  }

  private _doOpenChat(): void {
    console.log('[Crisp] openChat() called — window.$crisp:', !!window.$crisp, 'isArray:', Array.isArray(window.$crisp), 'isServiceReady:', this.isServiceReady);

    if (!window.$crisp) {
      console.error('[Crisp] openChat() — window.$crisp is not available');
      log.error('[CrispChatService] openChat() called but window.$crisp is not available');
      return;
    }

    try {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
      console.log('[Crisp] Pushed chat:show + chat:open');
      log.debug('Chat opened');
    } catch (error) {
      console.error('[Crisp] Failed to open chat:', error);
      log.error('Failed to open Crisp chat:', error);
    }
  }

  closeChat(): void {
    if (!this.isReady()) {
      return;
    }

    try {
      window.$crisp.push(['do', 'chat:close']);
      window.$crisp.push(['do', 'chat:hide']);
      log.debug('Chat closed');
    } catch (error) {
      log.error('Failed to close Crisp chat:', error);
    }
  }

  setFloatingButtonVisible(visible: boolean): void {
    if (!this.isReady()) {
      return;
    }

    try {
      if (visible) {
        window.$crisp.push(['do', 'chat:show']);
      } else {
        window.$crisp.push(['do', 'chat:hide']);
      }
    } catch (error) {
      log.error('Failed to set Crisp button visibility:', error);
    }
  }

  setUser(user: {
    id?: string;
    email?: string;
    name?: string;
    plan?: string;
    customData?: Record<string, any>;
  }): void {
    if (!this.isReady()) {
      return;
    }

    try {
      if (user.email) {
        window.$crisp.push(['set', 'user:email', [user.email]]);
      }
      if (user.name) {
        window.$crisp.push(['set', 'user:nickname', [user.name]]);
      }
      if (user.plan) {
        window.$crisp.push(['set', 'session:data', [[['plan', user.plan]]]]);
      }
      if (user.customData) {
        const dataEntries = Object.entries(user.customData).map(([key, value]) => [key, value]);
        window.$crisp.push(['set', 'session:data', [dataEntries]]);
      }
    } catch (error) {
      log.error('Failed to set Crisp user info:', error);
    }
  }

  sendMessage(message: string): void {
    if (!this.isReady()) {
      log.warn('Crisp chat service not ready');
      return;
    }

    try {
      window.$crisp.push(['do', 'message:send', ['text', message]]);
    } catch (error) {
      log.error('Failed to send message via Crisp:', error);
    }
  }

  sendFirstLoginMessage(userName?: string): void {
    if (!this.isReady()) {
      log.warn('Crisp chat service not ready - cannot send first login message');
      return;
    }

    try {
      const message = "Hey, I'm Chandler. If you have any questions about River or how it works, please don't hesitate to ask!";
      window.$crisp.push(['do', 'message:show', ['text', message]]);
      log.info('First-login welcome message sent to Crisp');
    } catch (error) {
      log.error('Failed to send first-login message via Crisp:', error);
    }
  }

  reset(): void {
    if (!this.isReady()) {
      return;
    }

    try {
      window.$crisp.push(['do', 'session:reset']);
    } catch (error) {
      log.error('Failed to reset Crisp session:', error);
    }
  }

  cleanup(): void {
    try {
      if (this.isReady()) {
        window.$crisp.push(['do', 'session:reset']);
        window.$crisp.push(['do', 'chat:close']);
      }
      this.isServiceReady = false;
    } catch (error) {
      log.error('Failed to cleanup Crisp service:', error);
    }
  }
}

export { CrispChatService };
export default CrispChatService;
